import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLang } from './LangContext';

const SITE_NAME = 'Pomodoro Flow';
const SITE_URL = 'https://www.pomodoro-flow.click';
const DEFAULT_IMAGE = `${SITE_URL}/pomodoro_logo.png`;

const ROUTE_SEO = {
  '/': {
    de: {
      title: 'Pomodoro Flow, kostenloser Online Pomodoro Timer für Fokus und Produktivität',
      description: 'Pomodoro Flow ist ein kostenloser Online Pomodoro Timer für fokussiertes Arbeiten, klare Pausen und eine einfache, ablenkungsarme Produktivitätsroutine im Browser.',
    },
    en: {
      title: 'Pomodoro Flow, free online Pomodoro timer for focus and productivity',
      description: 'Pomodoro Flow is a free online Pomodoro timer for focused work, clear breaks, and a simple low-distraction productivity routine in the browser.',
    },
  },
  '/about': {
    de: {
      title: 'Über Pomodoro Flow, Fokus Timer und Produktivitätsansatz',
      description: 'Erfahre mehr über Pomodoro Flow, den Fokus auf eine klare Timer-Erfahrung und den Ansatz hinter dem minimalistischen Pomodoro Workflow.',
    },
    en: {
      title: 'About Pomodoro Flow, focus timer and productivity approach',
      description: 'Learn more about Pomodoro Flow, its focus on a clear timer experience, and the approach behind this minimalist Pomodoro workflow.',
    },
  },
  '/contact': {
    de: {
      title: 'Kontakt, Pomodoro Flow',
      description: 'Kontaktseite für Pomodoro Flow, Fragen, Feedback und Rückmeldungen zum Online Pomodoro Timer und zur Website.',
    },
    en: {
      title: 'Contact, Pomodoro Flow',
      description: 'Contact page for Pomodoro Flow, including feedback, questions, and notes about the online Pomodoro timer and website.',
    },
  },
  '/privacy': {
    de: {
      title: 'Datenschutz, Pomodoro Flow',
      description: 'Datenschutzinformationen für Pomodoro Flow, inklusive Hinweisen zu Website-Nutzung, Consent und datenschutzrelevanten Diensten.',
    },
    en: {
      title: 'Privacy, Pomodoro Flow',
      description: 'Privacy information for Pomodoro Flow, including website usage, consent handling, and privacy-relevant services.',
    },
  },
  '/imprint': {
    de: {
      title: 'Impressum, Pomodoro Flow',
      description: 'Impressum von Pomodoro Flow mit den rechtlichen Anbieterinformationen zur Website und zum Online Pomodoro Timer.',
    },
    en: {
      title: 'Imprint, Pomodoro Flow',
      description: 'Imprint page for Pomodoro Flow with the legal publisher information for the website and online Pomodoro timer.',
    },
  },
};

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLink(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertJsonLd(id, payload) {
  let element = document.head.querySelector(`#${id}`);

  if (!element) {
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.id = id;
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(payload);
}

export default function SeoHead() {
  const { lang } = useLang();
  const location = useLocation();
  const pathname = location.pathname === '/index.html' ? '/' : location.pathname;
  const routeSeo = ROUTE_SEO[pathname] || ROUTE_SEO['/'];
  const localeSeo = routeSeo[lang] || routeSeo.en;
  const canonicalUrl = `${SITE_URL}${pathname === '/' ? '/' : pathname}`;
  const alternateDe = `${SITE_URL}${pathname === '/' ? '/' : pathname}?lang=de`;
  const alternateEn = `${SITE_URL}${pathname === '/' ? '/' : pathname}?lang=en`;
  const pageTitle = `${localeSeo.title} | ${SITE_NAME}`;
  const locale = lang === 'de' ? 'de_DE' : 'en_US';
  const alternateLocale = lang === 'de' ? 'en_US' : 'de_DE';

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = pageTitle;

    upsertMeta('meta[name="description"]', { name: 'description', content: localeSeo.description });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: pageTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: localeSeo.description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: locale });
    upsertMeta('meta[property="og:locale:alternate"]', { property: 'og:locale:alternate', content: alternateLocale });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: DEFAULT_IMAGE });
    upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: SITE_NAME });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: pageTitle });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: localeSeo.description });
    upsertMeta('meta[name="twitter:url"]', { name: 'twitter:url', content: canonicalUrl });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: DEFAULT_IMAGE });
    upsertMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: SITE_NAME });

    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl });
    upsertLink('link[rel="alternate"][hreflang="de"]', { rel: 'alternate', hreflang: 'de', href: alternateDe });
    upsertLink('link[rel="alternate"][hreflang="en"]', { rel: 'alternate', hreflang: 'en', href: alternateEn });
    upsertLink('link[rel="alternate"][hreflang="x-default"]', { rel: 'alternate', hreflang: 'x-default', href: canonicalUrl });

    upsertJsonLd('seo-jsonld-webapp', {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: SITE_NAME,
      url: canonicalUrl,
      description: localeSeo.description,
      applicationCategory: 'ProductivityApplication',
      operatingSystem: 'Web Browser',
      inLanguage: [ 'de', 'en' ],
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'EUR',
      },
      image: DEFAULT_IMAGE,
      author: {
        '@type': 'Person',
        name: 'Tobias Mai',
      },
    });
  }, [canonicalUrl, lang, locale, localeSeo.description, pageTitle, alternateLocale, alternateDe, alternateEn]);

  return null;
}
